import { z } from 'zod';

/* Contratos do support chat (Epic 28 / ADR-0352). Persistência em Postgres; o Durable
   Object faz o realtime. O MESMO protocolo WS é consumido por 28-14 (admin-web) e 28-16
   (desktop) — fixado aqui p/ evitar drift. */

export const ticketStatus = z.enum(['open', 'in_progress', 'closed']);
export const ticketPriority = z.enum(['low', 'normal', 'high', 'urgent']);

/* Host público dos anexos no R2 (ADR-0352/0431). Fonte única — validador + backend
   compõem a URL a partir daqui. */
export const R2_ATTACHMENT_BASE = 'https://r2.planuze.dev';

/* Attachments só do R2 do projeto (ADR-0352): bloqueia URL arbitrária do user. */
const r2AttachmentUrl = z.string().url().startsWith(`${R2_ATTACHMENT_BASE}/`);
const MAX_ATTACHMENTS = 5;
const MAX_MESSAGE_CHARS = 10_000;
/* Teto do id de correlação otimista (ADR-0434): client gera um id curto (`pending-<uuid>`)
   no envio; o DO ecoa de volta no broadcast p/ o remetente casar a bolha otimista com a
   mensagem persistida. Só correlação — nunca vira o id real da mensagem. */
const MAX_CLIENT_MESSAGE_ID_CHARS = 100;
/* Upload de anexo (ADR-0431): binário trafega como data URL base64 por IPC; 8 MiB infla
   ~33% em base64, daí o teto de chars. */
const MAX_ATTACHMENT_DATAURL_CHARS = 12_000_000;

export const messageDto = z.object({
  id: z.string(),
  ticketId: z.string(),
  senderType: z.enum(['user', 'staff']),
  senderId: z.string(),
  content: z.string(),
  attachments: z.array(z.string()),
  createdAt: z.string(),
});
export type MessageDto = z.infer<typeof messageDto>;

export const ticketDto = z.object({
  id: z.string(),
  userId: z.string(),
  orgId: z.string().nullable(),
  subject: z.string(),
  status: ticketStatus,
  priority: ticketPriority,
  assignedToStaffId: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
  lastStaffReplyAt: z.string().nullable(),
});
export type TicketDto = z.infer<typeof ticketDto>;

export const ticketDetailDto = ticketDto.extend({ messages: z.array(messageDto) });
export type TicketDetailDto = z.infer<typeof ticketDetailDto>;

/* Faceta de atribuição da inbox de staff (ADR-0352). `mine` é resolvido p/ o staff
   logado NO handler (self-scope — o cliente não escolhe o alvo); `unassigned` = fila
   sem atendente (`assigned_to_staff_id IS NULL`); `all`/ausente = sem filtro. */
export const ticketAssignmentFilter = z.enum(['mine', 'unassigned', 'all']);
export type TicketAssignmentFilter = z.infer<typeof ticketAssignmentFilter>;

/* Origem do ticket: `org` = pertence a uma organização (`org_id` presente), `individual`
   = conta pessoal (`org_id` nulo). Ausente = ambos. */
export const ticketSegmentFilter = z.enum(['org', 'individual']);
export type TicketSegmentFilter = z.infer<typeof ticketSegmentFilter>;

/* Listagem de tickets da inbox de staff (ADR-0352). Todas as facetas são server-side:
   filtrar client-side sobre as páginas já carregadas perdia itens nas páginas ainda não
   buscadas. Espelha o padrão de `listUsersQuery` (busca por substring + cursor keyset). */
export const listTicketsQuery = z.object({
  status: ticketStatus.optional(),
  priority: ticketPriority.optional(),
  assignment: ticketAssignmentFilter.optional(),
  /* Substring do assunto (ILIKE server-side). Piso 2 chars (espelha `listUsersQuery`). */
  search: z.string().min(2).max(200).optional(),
  /* Janela de criação [createdFrom, createdTo) em ISO — substitui o período client-side. */
  createdFrom: z.string().datetime().optional(),
  createdTo: z.string().datetime().optional(),
  segment: ticketSegmentFilter.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListTicketsQuery = z.infer<typeof listTicketsQuery>;

/* Item da inbox de staff: o `ticketDto` traz só IDs (`userId`/`assignedToStaffId`); o
   handler enriquece com o e-mail do usuário (auth via HMAC, best-effort → `null` se
   indisponível) e do atendente (LEFT JOIN local em `staff_users` → `null` se sem
   atribuição/ator órfão). A tabela exibe e-mail e cai no id só quando `null`. */
export const adminTicketListItemDto = ticketDto.extend({
  userEmail: z.string().nullable(),
  assignedToStaffEmail: z.string().nullable(),
});
export type AdminTicketListItem = z.infer<typeof adminTicketListItemDto>;

export const adminTicketsPage = z.object({
  items: z.array(adminTicketListItemDto),
  nextCursor: z.string().nullish(),
});
export type AdminTicketsPage = z.infer<typeof adminTicketsPage>;

export const assignTicketBody = z.object({ assignedToStaffId: z.string().min(1) });
export type AssignTicketBody = z.infer<typeof assignTicketBody>;

export const closeTicketBody = z.object({ closedReason: z.string().min(10).max(500) });
export type CloseTicketBody = z.infer<typeof closeTicketBody>;

/* Lotes homogêneos da inbox (ADR-0820). `assign` é deliberadamente self-scoped: o service
   resolve o staff da sessão, então o cliente não consegue atribuir em nome de outro operador. */
export const TICKET_BULK_MAX_IDS = 100;
const ticketBulkIds = z
  .array(z.string().min(1))
  .min(1)
  .max(TICKET_BULK_MAX_IDS)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' });
export const bulkTicketActionBody = z.discriminatedUnion('action', [
  z.object({ ids: ticketBulkIds, action: z.literal('assign') }),
  closeTicketBody.extend({ ids: ticketBulkIds, action: z.literal('close') }),
]);
export type BulkTicketActionBody = z.infer<typeof bulkTicketActionBody>;

export const bulkTicketActionResponseDto = z.object({
  results: z.array(z.object({ id: z.string(), ok: z.boolean(), error: z.string().nullable() })),
});
export type BulkTicketActionResponse = z.infer<typeof bulkTicketActionResponseDto>;

/* Lado-user (28-16): cria ticket + lista os próprios. Auth = JWT no api-gateway; o user
   NUNCA informa userId (vem do `x-planuze-user` injetado pelo gateway). */
/* Fluxo chat-first (ADR-0432): a 1ª mensagem cria o ticket e o assunto é derivado dela —
   uma mensagem de chat pode ser curta ("erro 500"), então o piso é 1 (não os 5/10 do
   antigo formulário). Só `max` continua protegendo contra abuso. */
export const createTicketBody = z.object({
  subject: z.string().min(1).max(200),
  priority: ticketPriority.default('normal'),
  content: z.string().min(1).max(MAX_MESSAGE_CHARS),
});
export type CreateTicketBody = z.infer<typeof createTicketBody>;

export const createTicketResult = z.object({ ticketId: z.string() });
export type CreateTicketResult = z.infer<typeof createTicketResult>;

/* Upload de anexo (ADR-0431): renderer manda data URL base64; backend grava no R2 e
   devolve a URL pública (host travado) + metadados p/ render. */
export const uploadAttachmentBody = z.object({
  dataUrl: z.string().min(1).max(MAX_ATTACHMENT_DATAURL_CHARS),
  name: z.string().min(1).max(200),
});
export type UploadAttachmentBody = z.infer<typeof uploadAttachmentBody>;

export const attachmentUpload = z.object({
  url: r2AttachmentUrl,
  name: z.string(),
  contentType: z.string(),
  size: z.number().int().nonnegative(),
});
export type AttachmentUpload = z.infer<typeof attachmentUpload>;

export const myTicketsQuery = z.object({
  status: ticketStatus.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type MyTicketsQuery = z.infer<typeof myTicketsQuery>;

export const ticketsPage = z.object({
  items: z.array(ticketDto),
  nextCursor: z.string().nullish(),
});
export type TicketsPage = z.infer<typeof ticketsPage>;

/* Protocolo WebSocket (ADR-0352). `subscribe` é implícito no upgrade (ticketId no path).
   `attachments` usa factory default (não shared mutable, anti-pattern §4). */
export const wsClientEvent = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('message'),
    content: z.string().min(1).max(MAX_MESSAGE_CHARS),
    attachments: z
      .array(r2AttachmentUrl)
      .max(MAX_ATTACHMENTS)
      .default(() => []),
    clientMessageId: z.string().min(1).max(MAX_CLIENT_MESSAGE_ID_CHARS).optional(),
  }),
  z.object({ type: z.literal('typing'), isTyping: z.boolean() }),
  z.object({ type: z.literal('mark_read'), messageId: z.string().min(1) }),
]);
export type WsClientEvent = z.infer<typeof wsClientEvent>;

export const wsServerEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('history'), messages: z.array(messageDto) }),
  /* `clientMessageId` (ADR-0434) só volta no eco para o remetente — é o id de correlação
     que ele enviou. Ausente nas mensagens originadas do outro lado. */
  z.object({
    type: z.literal('message'),
    message: messageDto,
    clientMessageId: z.string().min(1).max(MAX_CLIENT_MESSAGE_ID_CHARS).optional(),
  }),
  z.object({ type: z.literal('typing'), senderId: z.string(), isTyping: z.boolean() }),
  z.object({ type: z.literal('presence'), staffIds: z.array(z.string()), userOnline: z.boolean() }),
  /* Lifecycle do ticket (ADR-0433): atender/fechar/auto-atender → ambos os lados e a
     lista refletem em tempo real, sem reload. */
  z.object({
    type: z.literal('status'),
    status: ticketStatus,
    assignedToStaffId: z.string().nullable(),
  }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
]);
export type WsServerEvent = z.infer<typeof wsServerEvent>;

/* Payload do notify interno (rota REST → stub do SupportChatDO) que dispara o broadcast
   de `status`. O DO não é endereçável publicamente; só o Worker gated alcança o stub. */
export const ticketStatusNotify = z.object({
  status: ticketStatus,
  assignedToStaffId: z.string().nullable(),
});
export type TicketStatusNotify = z.infer<typeof ticketStatusNotify>;
